export type TransportCertainty = "definite-failure" | "unknown";
export type ShopApiFailureCode = "OFFLINE" | "NETWORK" | "HTTP" | "INVALID_RESPONSE" | "GRAPHQL";

export class ShopApiRequestError extends Error {
  constructor(
    public readonly certainty: TransportCertainty,
    public readonly code: ShopApiFailureCode,
    message: string,
    public readonly cause?: unknown,
    public readonly status?: number,
  ) {
    super(message);
    this.name = "ShopApiRequestError";
  }
}

export type GraphqlErrorShape = {
  message?: unknown;
  extensions?: unknown;
};

export type GraphqlEnvelope = {
  data?: unknown;
  errors?: GraphqlErrorShape[];
};

export interface ShopApiGraphqlClient {
  execute(input: {
    operationName: string;
    document: string;
    variables?: Record<string, unknown>;
    signal?: AbortSignal;
  }): Promise<unknown>;
}

export interface ShopApiAuthTokenStore {
  read(): string | null;
  write(token: string): void;
}

export type ShopApiGraphqlClientOptions = {
  endpoint: string;
  fetchImpl?: typeof fetch;
  credentials?: RequestCredentials;
  headers?: HeadersInit | (() => HeadersInit);
  isOnline?: () => boolean;
  authTokenStore?: ShopApiAuthTokenStore | null;
  requestTimeoutMs?: number;
};

const DEFINITE_HTTP_FAILURES = new Set([400, 401, 403, 404, 405, 413, 415]);
const BROWSER_AUTH_TOKEN_KEY = "happyprint:vendure-auth-token";

function browserAuthTokenStore(endpoint: string): ShopApiAuthTokenStore | null {
  // Same-origin deployments keep using Vendure's httpOnly cookie. The bearer
  // fallback is only needed when a separately-hosted storefront calls an
  // absolute Shop API URL and browsers may reject third-party cookies.
  if (!/^https?:\/\//i.test(endpoint) || typeof window === "undefined") return null;
  try {
    const storage = window.localStorage;
    return {
      read: () => storage.getItem(BROWSER_AUTH_TOKEN_KEY),
      write: token => storage.setItem(BROWSER_AUTH_TOKEN_KEY, token),
    };
  } catch {
    return null;
  }
}

function errorMessage(errors: GraphqlErrorShape[]): string {
  const first = errors.find(item => typeof item.message === "string")?.message;
  return typeof first === "string" ? first : "GraphQL request failed";
}

/**
 * Low-level Shop API transport. It is deliberately conservative for mutations:
 * once a request may have left the browser, a network/5xx/invalid-response failure
 * is UNKNOWN unless the server proves otherwise. Unsafe automatic retry is worse
 * than an unnecessary resolution check.
 */
export function createShopApiGraphqlClient(options: ShopApiGraphqlClientOptions): ShopApiGraphqlClient {
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  const credentials = options.credentials ?? "include";
  const isOnline = options.isOnline ?? (() => typeof navigator === "undefined" || navigator.onLine !== false);
  const authTokenStore = options.authTokenStore === undefined
    ? browserAuthTokenStore(options.endpoint)
    : options.authTokenStore;
  const requestTimeoutMs = options.requestTimeoutMs ?? 60_000;

  return {
    async execute({ operationName, document, variables = {}, signal }): Promise<unknown> {
      if (!isOnline()) {
        throw new ShopApiRequestError("definite-failure", "OFFLINE", "Device is offline before dispatch");
      }
      if (signal?.aborted) {
        throw new ShopApiRequestError("definite-failure", "NETWORK", "Request was aborted before dispatch");
      }

      let body: string;
      try {
        body = JSON.stringify({ operationName, query: document, variables });
      } catch (cause) {
        throw new ShopApiRequestError("definite-failure", "INVALID_RESPONSE", "Could not serialize GraphQL request", cause);
      }

      const configuredHeaders = typeof options.headers === "function" ? options.headers() : options.headers;
      const headers = new Headers(configuredHeaders);
      headers.set("content-type", "application/json");
      const storedAuthToken = authTokenStore?.read()?.trim();
      if (storedAuthToken && !headers.has("authorization")) {
        headers.set("authorization", `Bearer ${storedAuthToken}`);
      }
      // Vendure's CSRF prevention requires a preflight marker for relevant clients.
      // Sending it consistently is harmless and keeps the adapter compatible when
      // apiOptions.csrfPrevention is enabled.
      if (!headers.has("Apollo-Require-Preflight")) headers.set("Apollo-Require-Preflight", "true");

      const requestController = new AbortController();
      let timedOut = false;
      const forwardAbort = () => requestController.abort(signal?.reason);
      if (signal) signal.addEventListener("abort", forwardAbort, { once: true });
      const timeout = requestTimeoutMs > 0
        ? setTimeout(() => {
            timedOut = true;
            requestController.abort();
          }, requestTimeoutMs)
        : null;

      let response: Response;
      try {
        response = await fetchImpl(options.endpoint, {
          method: "POST",
          credentials,
          headers,
          body,
          signal: requestController.signal,
        });
      } catch (cause) {
        // Browser fetch does not tell us whether bytes reached the server.
        const message = timedOut
          ? `Shop API request timed out after ${requestTimeoutMs}ms`
          : "Network failure after dispatch may have committed";
        throw new ShopApiRequestError("unknown", "NETWORK", message, cause);
      } finally {
        if (timeout !== null) clearTimeout(timeout);
        if (signal) signal.removeEventListener("abort", forwardAbort);
      }

      const responseAuthToken = response.headers.get("vendure-auth-token")?.trim();
      if (responseAuthToken) authTokenStore?.write(responseAuthToken);

      if (!response.ok) {
        const certainty: TransportCertainty = DEFINITE_HTTP_FAILURES.has(response.status)
          ? "definite-failure"
          : "unknown";
        throw new ShopApiRequestError(
          certainty,
          "HTTP",
          `Shop API returned HTTP ${response.status}`,
          undefined,
          response.status,
        );
      }

      let envelope: GraphqlEnvelope;
      try {
        envelope = await response.json() as GraphqlEnvelope;
      } catch (cause) {
        throw new ShopApiRequestError("unknown", "INVALID_RESPONSE", "Shop API returned invalid JSON", cause, response.status);
      }

      if (Array.isArray(envelope.errors) && envelope.errors.length > 0) {
        // Any GraphQL execution error is fail-closed. Even if partial data exists,
        // a mutation may have produced side effects before another field failed.
        throw new ShopApiRequestError("unknown", "GRAPHQL", errorMessage(envelope.errors), envelope.errors, response.status);
      }

      if (envelope.data == null) {
        throw new ShopApiRequestError("unknown", "INVALID_RESPONSE", "Shop API response has no data", envelope, response.status);
      }

      return envelope.data;
    },
  };
}
